/* eslint-disable @typescript-eslint/no-unsafe-argument */
import React, { useState, useEffect, useMemo } from 'react';

import { Box } from '@mui/material';
import { Table, createColumnHelper, useTableSelection } from '@bestdoctor/spectrum-uikit/Table';
import type { ColumnDef } from '@tanstack/react-table';
import { Text } from '@bestdoctor/spectrum-uikit/Text';
import IconButton from '@mui/material/IconButton';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { InfoStaffModal } from 'shared/modals/InfoStaffModal/InfoStaffModal';
import { Loader } from 'shared/loaders/Loader';

import {
  PatientDataParams,
  IPatientForTable,
  ITablePatientsProps,
} from 'interfaces/patients/patientInterfaces';
import {
  slotsTableStatusCellStyle,
} from 'ui/tables/tables';
import { MAX_EMAIL_LENGTH, MAX_CONTENT_LENGTH, requiredTableFields } from 'utils/constants';
import { lineBreaker } from 'utils/lineBreaker';
import { truncateText } from 'utils/truncateText';
import { TooltipActionsInfo } from 'ui/tooltips/tooltipAction';
import { Stack } from '@mui/system';
import { writeToClipboard } from 'utils/writeToClipboard';
import { useSnackbar } from 'notistack';
import { useGetOnePatient } from 'hooks/patients/useGetOnePatient';
import { ErrorData } from 'interfaces/global/globalInterfaces';
import { useDebounce } from 'utils/useDebounce';

const columnHelper = createColumnHelper<IPatientForTable>();

export const TableStaff: React.FC<ITablePatientsProps> = ({
  selectedCompanies,
  handleCloseTableTooltip,
  isPatients,
  orderingTableStaff,
  tableUserFields,
  handleCloseTypeSearchTooltip,

  isFetchingNextPageSearchPatients,
  isAddingForbiddenByDates,
  isDetachingForbiddenByDates,

  handleStaffCheck,
  setSelectedStaff,
  handleAvailabilityCompletedPatients,

  handleOrderingTableStaff,
  handleActionWithStaffTooltip,
  accessList,
  rolesData,
  defaultPermissions,
  isSlotsAviable,
  refetchSearchPatients,
}) => {
  const [patientDataTable, setPatientDataTable] = useState<string | null>(null);
  const [patientType, setPatientType] = useState<string | null>(null);
  const [patientData, setPatientData] = useState<PatientDataParams | null>(null);

  const patientDataWithPatientType = useMemo<PatientDataParams | null>(
    () => (patientData ? { ...patientData, patientType: { text: '', value: patientType ?? '' } } : null),
    [patientData, patientType]
  );

  const { enqueueSnackbar } = useSnackbar();

  const { isLoadingPatient, isErrorPatient, refetchPatient } = useGetOnePatient({
    patient: patientDataTable,
    onSuccess: (data) => setPatientData(data),
    onError: (res) => {
      if (res.response.status >= 400 && res.response.status < 500) {
        res.response
          .json()
          .then((result) =>
            result.errors.forEach((item: ErrorData) =>
              enqueueSnackbar(item.message || 'Произошла ошибка', { variant: 'error' })
            )
          );
      }
    },
  });

  const [dataTypeChecking, setDataTypeChecking] = useState<string[]>([]);
  const [isInfoStaffModal, setInfoStaffModal] = useState(false);

  const rows: IPatientForTable[] = useMemo(() => isPatients || [], [isPatients]);

  // Row Selection
  const selection = useTableSelection<IPatientForTable>({
    idField: 'uuid',
  });

  // Синхронизируем selection с выбранными пациентами
  const prevSelectionRef = React.useRef<Record<string, boolean>>({});
  
  React.useEffect(() => {
    const currentSelection = selection.rowSelection;
    const prevSelection = prevSelectionRef.current;
    
    const currentKeys = Object.keys(currentSelection).filter((key) => currentSelection[key]);
    const prevKeys = Object.keys(prevSelection).filter((key) => prevSelection[key]);
    
    if (
      currentKeys.length !== prevKeys.length ||
      currentKeys.some((key) => !prevKeys.includes(key)) ||
      prevKeys.some((key) => !currentKeys.includes(key))
    ) {
      setSelectedStaff(currentKeys as string[]);
      
      // Обновляем dataTypeChecking на основе выбранных строк
      const selectedRows = rows.filter((row) => currentKeys.includes(row.uuid));
      const newDataTypeChecking = selectedRows.map((row) => row.type);
      setDataTypeChecking(newDataTypeChecking);
    }
    
    prevSelectionRef.current = currentSelection;
  }, [selection.rowSelection, rows, setSelectedStaff]);

  // Сортировка
  const [sorting, setSorting] = useState<{ id: string; desc: boolean }[]>([]);

  useEffect(() => {
    if (orderingTableStaff) {
      const isDesc = orderingTableStaff.startsWith('-');
      const fieldId = isDesc ? orderingTableStaff.slice(1) : orderingTableStaff;
      setSorting([{ id: fieldId, desc: isDesc }]);
    } else {
      setSorting([]);
    }
  }, [orderingTableStaff]);

  const handleSortingChange = useMemo(
    () => (newSorting: { id: string; desc: boolean }[]) => {
      if (newSorting.length > 0) {
        const sort = newSorting[0];
        handleOrderingTableStaff(sort.desc ? `-${sort.id}` : sort.id);
      } else {
        handleOrderingTableStaff('');
      }
    },
    [handleOrderingTableStaff]
  );

  const tableSorting = useMemo(() => {
    if (sorting.length === 0) return undefined;
    return {
      state: sorting,
      setState: (updater: any) => {
        const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
        handleSortingChange(newSorting);
      },
    };
  }, [sorting, handleSortingChange]);

  const handleCopyToClipboard = (text?: string): void => {
    writeToClipboard(text);
    enqueueSnackbar('Скопировано в буфер обмена!', { variant: 'info' });
  };

  const handleGetPatients = useDebounce(refetchPatient, 0);

  const handleClickInfoButton = (evt: React.SyntheticEvent, patient: IPatientForTable): void => {
    evt.stopPropagation();

    if (patient.uuid !== patientDataTable) {
      setPatientData(null);
    }

    setPatientDataTable(patient.uuid);
    setPatientType(patient.patientType);

    setInfoStaffModal(true);

    handleGetPatients();

    handleActionWithStaffTooltip(false);

    handleCloseTypeSearchTooltip();

    handleCloseTableTooltip();
  };

  useEffect(() => {
    const selectedCount = Object.keys(selection.rowSelection).filter(
      (key) => selection.rowSelection[key]
    ).length;
    handleStaffCheck(selectedCount !== 0);
  }, [selection.rowSelection, handleStaffCheck]);

  useEffect(() => {
    const checkType = dataTypeChecking.some((element) => element === 'Завершенный');
    handleAvailabilityCompletedPatients(checkType);
  }, [dataTypeChecking, handleAvailabilityCompletedPatients]);

  // Функция для проверки, должна ли колонка отображаться
  const shouldShowColumn = (fieldId: string): boolean => {
    return (
      tableUserFields?.some((field) => field === fieldId) ||
      requiredTableFields?.some((field) => field.value === fieldId)
    );
  };

  // Создаем колонки динамически
  const columns = useMemo<ColumnDef<IPatientForTable, any>[]>(() => {
    const cols: ColumnDef<IPatientForTable, any>[] = [];

    // Юрлицо
    if (shouldShowColumn('program__service_period__company__title')) {
      cols.push(
        columnHelper.accessor('company', {
          id: 'program__service_period__company__title',
          header: () => <Text as="span" variant="body-m">Юрлицо</Text>,
          cell: (info) => (
            <Box
              onClick={(evt) => {
                evt.stopPropagation();
                handleClickInfoButton(evt, info.row.original);
              }}
              sx={{ cursor: 'pointer', width: '100%' }}
            >
              <Text as="span" variant="body-m">{info.getValue()}</Text>
            </Box>
          ),
          enableSorting: true,
          meta: {
            headerCellProps: {
              textVariant: 'body-m',
            },
          },
        }) as ColumnDef<IPatientForTable, any>
      );
    }

    // Имя (ID)
    if (shouldShowColumn('patient__last_name')) {
      cols.push(
        columnHelper.accessor('fullname', {
          id: 'patient__last_name',
          header: () => <Text as="span" variant="body-m">Имя (ID)</Text>,
          cell: (info) => {
            const row = info.row.original;
            return (
              <Box>
                <Text as="span" variant="body-m">{row.fullname}</Text>
                <Box
                  sx={{
                    margin: '0',
                    padding: '0',
                    color: 'rgba(62, 65, 77, 0.6)',
                  }}
                >
                  {`(ID: ${row.id})`}{' '}
                  {row.patientType === 'relative' && (
                    <Box
                      component="span"
                      sx={{
                        color: '#FF6D6D',
                        fontSize: '12px',
                      }}
                    >
                      Родственник
                    </Box>
                  )}
                </Box>
              </Box>
            );
          },
          enableSorting: true,
          meta: {
            headerCellProps: {
              textVariant: 'body-m',
            },
          },
        }) as ColumnDef<IPatientForTable, any>
      );
    }

    // Дата р.
    if (shouldShowColumn('patient__birth_date')) {
      cols.push(
        columnHelper.accessor('birthday', {
          id: 'patient__birth_date',
          header: () => <Text as="span" variant="body-m">Дата р.</Text>,
          cell: (info) => <Text as="span" variant="body-m">{info.getValue()}</Text>,
          enableSorting: true,
          meta: {
            headerCellProps: {
              textVariant: 'body-m',
            },
          },
        }) as ColumnDef<IPatientForTable, any>
      );
    }

    // Тип полиса
    if (shouldShowColumn('policy_type')) {
      cols.push(
        columnHelper.accessor('policyType', {
          id: 'policy_type',
          header: () => <Text as="span" variant="body-m">Тип полиса</Text>,
          cell: (info) => <Text as="span" variant="body-m">{info.getValue()}</Text>,
          enableSorting: true,
          meta: {
            headerCellProps: {
              textVariant: 'body-m',
            },
          },
        }) as ColumnDef<IPatientForTable, any>
      );
    }

    // Программа
    if (shouldShowColumn('program__title')) {
      cols.push(
        columnHelper.accessor('program', {
          id: 'program__title',
          header: () => <Text as="span" variant="body-m">Программа</Text>,
          cell: (info) => <Text as="span" variant="body-m">{lineBreaker(info.getValue())}</Text>,
          enableSorting: true,
          meta: {
            headerCellProps: {
              textVariant: 'body-m',
            },
          },
        }) as ColumnDef<IPatientForTable, any>
      );
    }

    // Период обслуживания
    if (shouldShowColumn('start_date')) {
      cols.push(
        columnHelper.accessor('period', {
          id: 'start_date',
          header: () => <Text as="span" variant="body-m">Период обслуживания</Text>,
          cell: (info) => <Text as="span" variant="body-m">{lineBreaker(info.getValue())}</Text>,
          enableSorting: true,
          meta: {
            headerCellProps: {
              textVariant: 'body-m',
            },
          },
        }) as ColumnDef<IPatientForTable, any>
      );
    }

    // Email
    if (shouldShowColumn('greetings_email__icontains')) {
      cols.push(
        columnHelper.accessor('greetings_email', {
          id: 'greetings_email__icontains',
          header: () => <Text as="span" variant="body-m">Email</Text>,
          cell: (info) => {
            const row = info.row.original;
            if (row.greetings_email?.length > MAX_EMAIL_LENGTH) {
              return (
                <TooltipActionsInfo
                  title={
                    <Box
                      component="span"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleCopyToClipboard(row.greetings_email);
                      }}
                      sx={{
                        cursor: 'pointer',
                      }}
                    >
                      {row.greetings_email}
                    </Box>
                  }
                  placement="left"
                >
                  <Text as="span" variant="body-m">
                    {truncateText(row.greetings_email, MAX_EMAIL_LENGTH)}
                  </Text>
                </TooltipActionsInfo>
              );
            }
            return <Text as="span" variant="body-m">{row.greetings_email}</Text>;
          },
          enableSorting: true,
          meta: {
            headerCellProps: {
              textVariant: 'body-m',
            },
          },
        }) as ColumnDef<IPatientForTable, any>
      );
    }

    // Номер полиса
    if (shouldShowColumn('policy_number__icontains')) {
      cols.push(
        columnHelper.accessor('policy_number', {
          id: 'policy_number__icontains',
          header: () => <Text as="span" variant="body-m">Номер полиса</Text>,
          cell: (info) => {
            const row = info.row.original;
            if (row.policy_number?.length > MAX_CONTENT_LENGTH) {
              return (
                <TooltipActionsInfo title={row.policy_number} placement="left">
                  <Text as="span" variant="body-m">
                    {truncateText(row.policy_number, MAX_CONTENT_LENGTH)}
                  </Text>
                </TooltipActionsInfo>
              );
            }
            return <Text as="span" variant="body-m">{row.policy_number}</Text>;
          },
          enableSorting: true,
          meta: {
            headerCellProps: {
              textVariant: 'body-m',
            },
          },
        }) as ColumnDef<IPatientForTable, any>
      );
    }

    // Табельный номер
    if (shouldShowColumn('number__icontains')) {
      cols.push(
        columnHelper.accessor('number', {
          id: 'number__icontains',
          header: () => <Text as="span" variant="body-m">Табельный номер</Text>,
          cell: (info) => {
            const row = info.row.original;
            if (row.number?.length > MAX_CONTENT_LENGTH) {
              return (
                <TooltipActionsInfo title={row.number} placement="left">
                  <Text as="span" variant="body-m">{truncateText(row.number, MAX_CONTENT_LENGTH)}</Text>
                </TooltipActionsInfo>
              );
            }
            return <Text as="span" variant="body-m">{row.number}</Text>;
          },
          enableSorting: true,
          meta: {
            headerCellProps: {
              textVariant: 'body-m',
            },
          },
        }) as ColumnDef<IPatientForTable, any>
      );
    }

    // Доп. информация
    if (shouldShowColumn('additional_info__icontains')) {
      cols.push(
        columnHelper.accessor('additional_info', {
          id: 'additional_info__icontains',
          header: () => <Text as="span" variant="body-m">Доп. информация</Text>,
          cell: (info) => {
            const row = info.row.original;
            if (row.additional_info?.length > MAX_CONTENT_LENGTH) {
              return (
                <TooltipActionsInfo title={row.additional_info} placement="left">
                  <Text as="span" variant="body-m">
                    {truncateText(row.additional_info, MAX_CONTENT_LENGTH)}
                  </Text>
                </TooltipActionsInfo>
              );
            }
            return <Text as="span" variant="body-m">{row.additional_info}</Text>;
          },
          enableSorting: true,
          meta: {
            headerCellProps: {
              textVariant: 'body-m',
            },
          },
        }) as ColumnDef<IPatientForTable, any>
      );
    }

    // Тип
    if (shouldShowColumn('_status')) {
      cols.push(
        columnHelper.accessor('type', {
          id: '_status',
          header: () => <Text as="span" variant="body-m">Тип</Text>,
          cell: (info) => {
            const row = info.row.original;
            return (
              <Box sx={() => slotsTableStatusCellStyle(row?.type)}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Text as="span" variant="body-m">{row.type}</Text>
                  <IconButton
                    onClick={(evt) => {
                      handleClickInfoButton(evt, row);
                    }}
                  >
                    <InfoOutlinedIcon fontSize="small" sx={{ color: '#BCBEC8' }} />
                  </IconButton>
                </Stack>
              </Box>
            );
          },
          enableSorting: true,
          meta: {
            headerCellProps: {
              textVariant: 'body-m',
            },
          },
        }) as ColumnDef<IPatientForTable, any>
      );
    }

    return cols;
  }, [tableUserFields, handleClickInfoButton, handleCopyToClipboard]);

  return (
    <>
      <Box sx={{ width: '100%', marginTop: '12px' }}>
        <Box
          sx={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            padding: '20px',
            width: '100%',
          }}
        >
          <Table
            data={rows}
            columns={columns}
            selectionProps={selection}
            sorting={tableSorting}
            enableSorting
          />
        </Box>
      </Box>

      {isFetchingNextPageSearchPatients && <Loader />}

      {isInfoStaffModal &&
        patientDataWithPatientType &&
        patientDataWithPatientType.uuid === patientDataTable && (
        <InfoStaffModal
          isAddingForbiddenByDates={isAddingForbiddenByDates}
          isDetachingForbiddenByDates={isDetachingForbiddenByDates}
          open={isInfoStaffModal}
          selectedCompanies={selectedCompanies}
          patientData={patientDataWithPatientType}
          isLoadingPatient={isLoadingPatient}
          isErrorPatient={isErrorPatient}
          handleClose={() => {
            setInfoStaffModal(false);
            setPatientType(null);
            setPatientData(null);
          }}
          refetchPatient={refetchPatient}
          accessList={accessList}
          rolesData={rolesData}
          defaultPermissions={defaultPermissions}
          isSlotsAviable={isSlotsAviable}
          refetchSearchPatients={refetchSearchPatients}
        />
      )}
    </>
  );
};
